import { Fragment, useEffect, useState } from 'react';
import { api, getToken, qs } from '../api/client';
import { urlImpression } from '../api/config';
import { Bandeau, Carte, Champ, Squelette, Vide } from '../components/ui';
import { dateHeureFr, roleLabel } from '../lib/format';
import { T } from '../i18n';

interface Ligne {
  _id: string; date: string; nomUtilisateur: string; role: string; action: string;
  entite: string; entiteId?: string; libelle: string;
  avant?: Record<string, unknown>; apres?: Record<string, unknown>; motif?: string; appareil?: string;
}
interface Reponse { lignes: Ligne[]; total: number; page: number; pages: number }

const COULEUR: Record<string, string> = {
  creer: 'var(--succes)', modifier: 'var(--attention)', annuler: 'var(--danger)',
  deroger: 'var(--violet)', cloturer: 'var(--primaire)', change: 'var(--violet)',
  restituer_caution: 'var(--primaire)', resilier: 'var(--danger)',
  faire_depart: 'var(--succes)', faire_retour: 'var(--primaire)',
  demander_correction: 'var(--attention)', accorder_correction: 'var(--succes)',
  refuser_correction: 'var(--danger)', signaler: 'var(--attention)',
  mettre_liste_noire: 'var(--danger)', retirer_liste_noire: 'var(--succes)',
};

/**
 * L'action et l'entite sont les cles exactes du journal — les toucher rendrait
 * le filtre muet et casserait la relecture des anciennes lignes. On ne change
 * que ce qui s'affiche : Hichem lit « Création », pas « creer ».
 */
const LIBELLE_ACTION = (): Record<string, string> => T.journal.actions;

const LIBELLE_ENTITE = (): Record<string, string> => T.journal.entites;

/**
 * Les entites exactement telles que le serveur les ecrit dans le journal.
 * Un libelle qui differe d'un seul accent rend le filtre muet : il cherche
 * « Réservation » alors que la base contient « Reservation ».
 * La correspondance est verifiee par scripts/verifier-langue.mjs.
 */
const ENTITES_JOURNAL = [
  'Amende',
  'CaisseSession',
  'Caution',
  'Client',
  'Contrat',
  'Devise',
  'Entretien',
  'EtatDesLieux',
  'Facture',
  'MouvementCaisse',
  'Note',
  'Paramètres',
  'Reservation',
  'Sinistre',
  'Supplément',
  'Système',
  'TauxChange',
  'Utilisateur',
  'Véhicule',
];

export default function Journal() {
  const [d, setD] = useState<Reponse | null>(null);
  const [page, setPage] = useState(1);
  const [entite, setEntite] = useState('');
  const [ouvert, setOuvert] = useState<string | null>(null);

  useEffect(() => { api.get<Reponse>(`/journal${qs({ page, entite })}`).then(setD); }, [page, entite]);
  if (!d) return <Squelette lignes={8} />;

  const tj = T.journal;
  return (
    <>
      <div className="entete">
        <div className="titres"><h1>{tj.titre}</h1><p>{tj.sousTitre(d.total)}</p></div>
        <div className="actions">
          <a className="btn" href={urlImpression('/api/journal/export', getToken())} onClick={(e) => {
            e.preventDefault();
            void api.get<string>('/journal/export').then((csv) => {
              const b = new Blob([csv], { type: 'text/csv;charset=utf-8' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(b); a.download = tj.fichier; a.click();
            });
          }}>{tj.exporter}</a>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <Bandeau type="info">
          {tj.inalterable}
        </Bandeau>
      </div>

      <Carte pad={false} action={
        <select value={entite} onChange={(e) => { setEntite(e.target.value); setPage(1); }}
                style={{ height: 32, borderRadius: 8, border: '1px solid var(--bord-fort)', padding: '0 8px' }}>
          <option value="">{tj.toutesEntites}</option>
          {ENTITES_JOURNAL.map((x) => <option key={x} value={x}>{LIBELLE_ENTITE()[x] ?? x}</option>)}
        </select>} titre={tj.entrees}>
        {d.lignes.length === 0 ? <div className="carte-p"><Vide titre={tj.aucune} /></div> : (
          <div className="enveloppe-tbl">
            <table className="tbl compact">
              <thead><tr><th>{tj.col.horodatage}</th><th>{tj.col.utilisateur}</th><th>{tj.col.action}</th><th>{tj.col.entite}</th><th>{tj.col.passe}</th><th /></tr></thead>
              <tbody>
                {d.lignes.map((l) => (
                  <Fragment key={l._id}>
                    <tr onClick={() => setOuvert(ouvert === l._id ? null : l._id)} style={{ cursor: 'pointer' }}>
                      <td className="num mini">{dateHeureFr(l.date)}</td>
                      <td><b>{l.nomUtilisateur}</b><div className="mini sourdine">{roleLabel(l.role)}</div></td>
                      <td><span style={{ color: COULEUR[l.action] ?? 'var(--texte2)', fontWeight: 600 }}>{LIBELLE_ACTION()[l.action] ?? l.action.replace(/_/g, ' ')}</span></td>
                      <td className="mini sourdine">{LIBELLE_ENTITE()[l.entite] ?? l.entite}</td>
                      <td>{l.libelle}{l.motif && <div className="mini sourdine">{tj.motif(l.motif)}</div>}</td>
                      <td className="droite mini sourdine">{(l.avant || l.apres) ? (ouvert === l._id ? '▾' : '▸') : ''}</td>
                    </tr>
                    {ouvert === l._id && (l.avant || l.apres) && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--zebre)', padding: 12 }}>
                          <div className="grille g2">
                            <div>
                              <div className="micro">{tj.avant}</div>
                              <pre style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(l.avant ?? {}, null, 2)}</pre>
                            </div>
                            <div>
                              <div className="micro">{tj.apres}</div>
                              <pre style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(l.apres ?? {}, null, 2)}</pre>
                            </div>
                          </div>
                          {l.appareil && <div className="mini sourdine" style={{ marginTop: 8 }}>{tj.appareil(l.appareil)}</div>}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      {d.pages > 1 && (
        <div className="actions" style={{ marginTop: 14, justifyContent: 'center' }}>
          <button className="btn btn-s" disabled={page <= 1} onClick={() => setPage(page - 1)}>{tj.precedent}</button>
          <span className="mini sourdine">{tj.page(d.page, d.pages)}</span>
          <button className="btn btn-s" disabled={page >= d.pages} onClick={() => setPage(page + 1)}>{tj.suivant}</button>
        </div>
      )}
    </>
  );
}
